import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../Services/supabaseClient";
import "./resetPassword.css";
import logo from "../components/Images/KostPadi-Icon.png";

const ResetPasswordUpdate = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw1, setShowPw1] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState(null); // {type:'error'|'success', msg:string}
  const navigate = useNavigate();

  // simple password strength display (for visual feedback only)
  const strength = useMemo(() => {
    let s = 0;
    if (newPassword.length >= 8) s++;
    if (/[A-Z]/.test(newPassword)) s++;
    if (/[a-z]/.test(newPassword)) s++;
    if (/\d/.test(newPassword)) s++;
    if (/[^A-Za-z0-9]/.test(newPassword)) s++;
    return Math.min(s, 4);
  }, [newPassword]);

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setBanner(null);
    if (newPassword !== confirmPassword) {
      setBanner({ type: "error", msg: "Passwords do not match!" });
      return;
    }
    setLoading(true);

    // Supabase automatically picks up the access token from the URL query param
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setLoading(false);

    if (error) {
      setBanner({
        type: "error",
        msg: "Error updating password: " + error.message,
      });
    } else {
      setBanner({
        type: "success",
        msg: "Password reset successful! Redirecting to login…",
      });
      setTimeout(() => navigate("/login"), 2000);
    }
  };

  return (
    <div className="auth-layout reset-layout">
      {/* Left Side (Brand / Info) */}
      <aside className="auth-aside">
        <div className="brand-head">
          <img src={logo} alt="KostPadi" />
          <h1>KostPadi</h1>
        </div>
        <p className="tagline">
          Secure and modern ERP platform for small businesses in Nigeria. Your
          data. Your control.
        </p>
        <ul className="pill-list">
          <li>Strong encryption</li>
          <li>Supabase RLS</li>
          <li>Cloud secured</li>
        </ul>
        <div className="aside-footer">
          <a href="/" className="aside-link">
            ← Back to site
          </a>
        </div>
      </aside>

      {/* Right Side (Reset Form) */}
      <main className="auth-main">
        <div className="reset-card">
          <header className="reset-head">
            <img src={logo} alt="KostPadi Logo" className="reset-logo" />
            <div>
              <h2>Set a new password</h2>
              <p className="muted">Choose a strong and secure password.</p>
            </div>
          </header>

          {banner && (
            <div
              className={
                banner.type === "error" ? "error-banner" : "success-banner"
              }
              role={banner.type === "error" ? "alert" : "status"}
            >
              {banner.msg}
            </div>
          )}

          <form onSubmit={handlePasswordUpdate} className="reset-form">
            <label>
              New Password
              <div className="pw-wrap">
                <input
                  type={showPw1 ? "text" : "password"}
                  placeholder="••••••••"
                  className="reset-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="pw-toggle"
                  aria-label={showPw1 ? "Hide password" : "Show password"}
                  onClick={() => setShowPw1((s) => !s)}
                >
                  {showPw1 ? "Hide" : "Show"}
                </button>
              </div>
              <div className={`pw-meter s${strength}`}>
                <span />
                <span />
                <span />
                <span />
                <em className="pw-label">
                  {["Too weak", "Weak", "Okay", "Good", "Strong"][strength]}
                </em>
              </div>
            </label>

            <label>
              Confirm Password
              <div className="pw-wrap">
                <input
                  type={showPw2 ? "text" : "password"}
                  placeholder="Re-enter password"
                  className="reset-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="pw-toggle"
                  aria-label={showPw2 ? "Hide password" : "Show password"}
                  onClick={() => setShowPw2((s) => !s)}
                >
                  {showPw2 ? "Hide" : "Show"}
                </button>
              </div>
            </label>

            <button type="submit" className="reset-btn" disabled={loading}>
              {loading ? "Updating…" : "Reset Password"}
            </button>

            <button
              type="button"
              className="back-btn"
              onClick={() => navigate("/login")}
            >
              ← Back to log in
            </button>

            <p className="tiny-note">
              Make sure your new password is at least 8 characters long and
              includes numbers, symbols, and both uppercase and lowercase
              letters.
            </p>
          </form>
        </div>
      </main>
    </div>
  );
};

export default ResetPasswordUpdate;
